import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  ajvResolver,
  ValidatorsProps,
  responseErrorsResolver,
  ResponseError,
  Popup,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearTariffsCreateFormState } from './store/slices/create-tariff';
import { TariffFormValues, TariffItem, TariffType } from './types';
import TariffsCreateTariffForm from '../../components/tariffs/create-tariff-form';
import { useCreateTariffMutation } from './store/services';

import { TariffsRoutes } from '../../routes';

const CreateTariffItemForm = () => {
  const {
    tariffs: {
      list: { validators },
    },
    configurationDictionaries: { activePermissions, riskScoreRanges },
  } = useAppSelector((state) => state);

  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
  } = useForm<TariffFormValues>({
    resolver: ajvResolver(validators?.createTariffSchema as ValidatorsProps),
    defaultValues: {
      name: '',
      riskScoreRangeId: undefined,
      type: TariffType.INDIVIDUAL,
    },
  });

  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [create, { isLoading, isSuccess }] = useCreateTariffMutation();

  const onFormSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const response = (await create(values)) as {
      data?: { data: TariffItem };
      error: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(`${TariffsRoutes.TARIFFS_LIST}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearTariffsCreateFormState());
    };
  }, [dispatch]);

  let content;

  if (activePermissions?.includes(PermissionEnum.TARIFFS_CREATE_ACTION)) {
    content = (
      <TariffsCreateTariffForm
        control={control}
        errors={errors}
        riskScoreRanges={riskScoreRanges}
      />
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title="Create new tariff"
        errorSidepanel={globalError}
        sidepanelExpandedKey="create-tariff"
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.TARIFFS_CREATE_ACTION,
            ) && (
              <ButtonPrimary
                text={isLoading ? 'Loading...' : 'Add tariff'}
                onClick={onFormSubmit}
                bold
              />
            )}
            <ButtonSecondary
              text="Canel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default CreateTariffItemForm;
